import React from 'react';
import { Download, BookOpen, Video, Headphones, FileText, ExternalLink } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const Resources = () => {
  const resources = [
    {
      category: 'Meditation Guides',
      icon: BookOpen,
      color: 'emerald',
      items: [
        { name: 'Beginner\'s Guide to Sunya Meditation', type: 'PDF', size: '2.5 MB' },
        { name: 'Reverse Counting Technique Manual', type: 'PDF', size: '1.8 MB' },
        { name: '7-Belt System Overview', type: 'PDF', size: '3.2 MB' },
        { name: 'Daily Practice Checklist', type: 'PDF', size: '0.5 MB' }
      ]
    },
    {
      category: 'Audio Meditations',
      icon: Headphones,
      color: 'blue',
      items: [
        { name: 'Guided Reverse Counting (15 min)', type: 'MP3', size: '12 MB' },
        { name: 'Beginner Sunya Session (20 min)', type: 'MP3', size: '18 MB' },
        { name: 'Advanced Practice Session (30 min)', type: 'MP3', size: '25 MB' },
        { name: 'Morning Meditation Routine', type: 'MP3', size: '15 MB' }
      ]
    },
    {
      category: 'Video Content',
      icon: Video,
      color: 'red',
      items: [
        { name: 'Introduction to Sunya Practice', type: 'Link', platform: 'YouTube' },
        { name: 'Complete Sunya Talk Series', type: 'Link', platform: 'YouTube' },
        { name: 'Ganotherapy Explained', type: 'Link', platform: 'YouTube' },
        { name: 'Shang Han Lun Teachings', type: 'Link', platform: 'YouTube' }
      ]
    },
    {
      category: 'Study Materials',
      icon: FileText,
      color: 'amber',
      items: [
        { name: 'Ancient Sutra References', type: 'PDF', size: '5.8 MB' },
        { name: 'TCM Fundamentals', type: 'PDF', size: '4.2 MB' },
        { name: 'Ganoderma Research Compilation', type: 'PDF', size: '8.5 MB' },
        { name: 'Ethical Foundations of Sunya', type: 'PDF', size: '1.5 MB' }
      ]
    }
  ];

  const externalLinks = [
    { name: 'DXN Official Website', url: 'https://www.dxn2u.com', description: 'Global DXN business and products' },
    { name: 'Sunyatee Foundation', url: 'https://www.sunyatee.com', description: 'Official foundation website' },
    { name: 'Sunya Studios YouTube', url: 'https://www.youtube.com/@sunyastudios', description: 'Video talks and teachings' },
    { name: 'Dr. Rajesh Savera Facebook', url: 'https://www.facebook.com', description: 'Co-teacher updates and content' }
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
      red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' },
      amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' }
    };
    return colors[color];
  };

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title="Resources - Meditation Guides & Study Materials"
        description="Download free Sunya meditation guides, audio meditations, TCM study materials, and educational content from Dr. Lim Siow Jin. Access ancient wisdom for modern practitioners."
        keywords="Sunya meditation guides, meditation audio, TCM study materials, Ganoderma research, herbal medicine resources, Dr. Lim Siow Jin resources"
        
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
              <Download className="text-white" size={40} />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Resources</h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
            Downloadable guides, audio meditations, study materials, and educational content
          </p>
          <div className="flex justify-center mt-6">
            <SocialShare 
              title="Resources - Dr. Lim Siow Jin"
              description="Download meditation guides, audio sessions, and study materials for your wellness journey."
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Resource Categories */}
        <div className="space-y-12">
          {resources.map((category, idx) => {
            const IconComponent = category.icon;
            const colors = getColorClasses(category.color);
            
            return (
              <div key={idx}>
                <div className="flex items-center space-x-3 mb-6">
                  <div className={`w-12 h-12 rounded-full ${colors.bg} flex items-center justify-center`}>
                    <IconComponent className={colors.text} size={24} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{category.category}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {category.items.map((item, itemIdx) => (
                    <div key={itemIdx} className={`bg-white rounded-xl p-6 shadow-md border ${colors.border} hover:shadow-lg transition-all cursor-pointer group`}>
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-gray-900 text-sm group-hover:text-emerald-700 transition-colors">
                          {item.name}
                        </h3>
                        <Download size={18} className="text-gray-400 group-hover:text-emerald-600 transition-colors flex-shrink-0" />
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className={`${colors.text} font-semibold`}>{item.type}</span>
                        {item.size && <span className="text-gray-500">{item.size}</span>}
                        {item.platform && <span className="text-gray-500">{item.platform}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* External Links */}
        <div className="mt-16">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <ExternalLink className="text-purple-600" size={24} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">External Links</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {externalLinks.map((link, idx) => (
              <a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl p-6 shadow-md border border-purple-200 hover:shadow-lg transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors">
                      {link.name}
                    </h3>
                    <p className="text-sm text-gray-600">{link.description}</p>
                  </div>
                  <ExternalLink size={20} className="text-purple-600 flex-shrink-0 ml-3" />
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-12 border border-emerald-200">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Get New Resources First</h2>
            <p className="text-gray-600 mb-8">
              Subscribe to receive notifications when new meditation guides, talks, and study materials are available
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-6 py-3 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button className="px-8 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resources;